import { getProxyBase } from '@/lib/proxy';
import { AccountApiNetworkError } from './errors';

export interface AccountUser {
  userId: string;
}

export interface AccountSyncRecord {
  store: string;
  key: string;
  ciphertext: string;
  iv: string;
  updatedAt: number;
  deletedAt?: number;
}

export interface PutAccountSyncRecordsInput {
  records: AccountSyncRecord[];
}

export interface AccountSyncKey {
  rawKey: string;
  createdAt: number;
}

export async function accountFetch<T, Body = unknown>(
  path: string,
  token: string,
  init: { method?: string; body?: Body } = {},
): Promise<T> {
  const headers = new Headers({
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  });
  if (init.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  const proxyBase = await getProxyBase();
  let res: Response;
  try {
    res = await fetch(`${proxyBase}${path}`, {
      method: init.method ?? 'GET',
      headers,
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new AccountApiNetworkError(`account API request failed: ${msg}`, proxyBase, {
      cause: err,
    });
  }
  if (!res.ok) {
    let detail = '';
    try {
      const body = (await res.json()) as { error?: unknown; note?: unknown };
      const error = typeof body.error === 'string' ? body.error : undefined;
      const note = typeof body.note === 'string' ? body.note : undefined;
      detail = [error, note].filter(Boolean).join(': ');
    } catch {
      // Non-JSON account errors are still surfaced by status below.
    }
    throw new Error(
      detail
        ? `account API returned ${res.status}: ${detail}`
        : `account API returned ${res.status}`,
    );
  }
  return (await res.json()) as T;
}

export function getAccountMe(token: string): Promise<AccountUser> {
  return accountFetch<AccountUser>('/account/me', token);
}

export function getOrCreateAccountSyncKey(token: string): Promise<AccountSyncKey> {
  return accountFetch<AccountSyncKey>('/account/sync-key', token, { method: 'POST' });
}

export async function listAccountSyncRecords(token: string): Promise<AccountSyncRecord[]> {
  const body = await accountFetch<{ records: AccountSyncRecord[] }>(
    '/account/sync-records',
    token,
  );
  return body.records;
}

export function putAccountSyncRecords(
  token: string,
  input: PutAccountSyncRecordsInput,
): Promise<{ ok: boolean }> {
  return accountFetch<{ ok: boolean }, PutAccountSyncRecordsInput>(
    '/account/sync-records',
    token,
    {
      method: 'PUT',
      body: input,
    },
  );
}

export function resetAccountSync(token: string): Promise<{ ok: boolean }> {
  return accountFetch('/account/reset', token, { method: 'DELETE' });
}
